/**
 * The district delivery mission (ADR-022): pure CONFIG DATA — the road
 * network, the delivery path with its turn cues, static props, decorative
 * modules, encounter zones — plus the factory that instantiates agents for a
 * given (variant, mirror, seed).
 *
 * Layout (logical coordinates, x east / z north; mirrored as a whole):
 *   A1  alley north from the start ........ research pedestrian, bike, cat
 *   J1  turn RIGHT into S1
 *   S1  two-way street east ............... parked cars, pull-out car, traffic
 *   R   roundabout (counter-clockwise) .... take the 3rd exit (north)
 *   S2  street north ...................... walking group, oncoming motorcycle
 *   J2  turn LEFT into A2
 *   A2  alley west to the customer ........ dog, vendor cart, final pair
 * Wrong branches (west of J1, roundabout exits 1-2, north of J2) are real
 * roads closed by construction barriers — never traps: the robot can always
 * turn around, and the guidance says so.
 *
 * Realism rules (validated by tests/unit/mission_validation.test.ts):
 *   - cars exist only on streets; they never enter or cross an alley;
 *   - only people and pets cross the road, and only in alleys;
 *   - every vehicle follows a lane toward a destination and leaves; none
 *     waits for the robot in order to cut across it.
 */

import type { Vec2 } from "../core/vec";
import {
  CatAgent,
  DogAgent,
  ResearchPedAgent,
  StandingPersonAgent,
  VehicleAgent,
  WalkerAgent,
  type AgentModel,
} from "./agents";
import type { EncounterConfig } from "./encounters";
import { snapLastToKerb } from "./lane_ribbon";
import { RoadNetwork, type RoadPiece, type RoadType } from "./network";
import { DeliveryPath, type PathWaypoint, type TurnKind } from "./path";
import type { Obstacle, ScenarioConfig, Variant } from "./types";

export const MISSION_TIMEOUT_S = 150;
export const MISSION_MAP_ID = "district_v2";

// ---------------------------------------------------------------------------
// Geometry constants (logical frame).

export const A1_HW = 1.8; // alley 3.6 m = 6x robot width
export const S1_Z = 84; // street 1 centerline
export const S1_HW = 4.5; // 9 m two-way street with parking
export const S1_LANE = 1.4; // robot's lane center, right of the centerline
export const S1_ONCOMING = -2.0;
export const RING: Vec2 = { x: 101, z: 84 };
export const RING_IN = 4.5;
export const RING_OUT = 11.5;
export const RING_LANE = 7.5; // circulating lane radius (4 m of run-wide room to the outer curb)
export const S2_X = 103; // offset east of the ring center so the exit is a gentle deflection
export const S2_HW = 3.5;
const S2_LANE = 1.2;
export const S2_ONCOMING = -1.1;
export const A2_Z = 136;
export const A2_HW = 1.6;
export const DEST_X = 44;

/** Ring point at circulation angle psi (deg) and radius r. */
function ringPt(psiDeg: number, r: number): Vec2 {
  const psi = (psiDeg * Math.PI) / 180;
  return { x: RING.x + Math.sin(psi) * r, z: RING.z + Math.cos(psi) * r };
}

/** Arc waypoints along the ring from psiFrom to psiTo (deg), counter-clockwise
 * = decreasing psi in the logical frame. */
export function ringArc(psiFromDeg: number, psiToDeg: number, r: number, stepDeg = 10): Vec2[] {
  const out: Vec2[] = [];
  const n = Math.max(1, Math.ceil(Math.abs(psiToDeg - psiFromDeg) / stepDeg));
  for (let i = 0; i <= n; i++) {
    out.push(ringPt(psiFromDeg + ((psiToDeg - psiFromDeg) * i) / n, r));
  }
  return out;
}



/**
 * Roundabout joins/leaves as real drivers take them: a straight chord from
 * the approach lane onto the circulating lane (and off it), so no path or
 * vehicle ever has to corner 80 deg at the ring's edge. Each is
 * [approach point, ring point]; the psi of the ring point is where the
 * ring arc starts or ends.
 */
export const JOIN = {
  westIn: { pts: [{ x: 90, z: S1_Z - S1_LANE }, ringPt(-135, RING_LANE)], psi: -135 },
  westOut: { pts: [ringPt(-60, RING_LANE), { x: 88, z: S1_Z - S1_ONCOMING }], psi: -60 },
  southIn: { pts: [{ x: RING.x + 1.2, z: 72 }, ringPt(150, RING_LANE)], psi: 150 },
  southOut: { pts: [ringPt(-150, RING_LANE), { x: RING.x - 1.2, z: 72 }], psi: -150 },
  eastIn: { pts: [{ x: 112, z: S1_Z + 1.2 }, ringPt(60, RING_LANE)], psi: 60 },
  eastOut: { pts: [ringPt(120, RING_LANE), { x: 114, z: S1_Z - 1.2 }], psi: 120 },
  northIn: { pts: [{ x: S2_X + S2_ONCOMING, z: 96 }, ringPt(-20, RING_LANE)], psi: -20 },
  northOut: { pts: [ringPt(45, RING_LANE), { x: S2_X + S2_LANE, z: 98 }], psi: 45 },
} as const;

// ---------------------------------------------------------------------------
// Road network.

/** Flared mouths where roads meet the ring: a real roundabout has no concave
 * curb pockets at its joins, and neither must this one (a fast robot running
 * wide on a join chord would otherwise wedge into the corner). */
const MOUTHS: RoadPiece[] = Object.entries(JOIN).map(([id, j]) => ({
  kind: "band" as const,
  id: `mouth_${id}`,
  roadType: "street" as const,
  pts: [...j.pts],
  halfWidth: 2.4,
  frame: false,
}));

export const PIECES: RoadPiece[] = [
  { kind: "band", id: "A1", roadType: "alley", pts: [{ x: 0, z: -8 }, { x: 0, z: S1_Z + 2.5 }], halfWidth: A1_HW },
  { kind: "band", id: "S1", roadType: "street", pts: [{ x: -44, z: S1_Z }, { x: 93, z: S1_Z }], halfWidth: S1_HW },
  { kind: "ring", id: "R", roadType: "roundabout", cx: RING.x, cz: RING.z, rIn: RING_IN, rOut: RING_OUT, dir: -1 },
  { kind: "band", id: "RS", roadType: "street", pts: [{ x: RING.x, z: 76 }, { x: RING.x, z: 40 }], halfWidth: 3.0 },
  { kind: "band", id: "RE", roadType: "street", pts: [{ x: 109, z: S1_Z }, { x: 150, z: S1_Z }], halfWidth: 3.0 },
  { kind: "band", id: "S2", roadType: "street", pts: [{ x: S2_X, z: 92 }, { x: S2_X, z: 170 }], halfWidth: S2_HW },
  { kind: "band", id: "A2", roadType: "alley", pts: [{ x: S2_X + 3.5, z: A2_Z }, { x: 38, z: A2_Z }], halfWidth: A2_HW },
  ...MOUTHS,
];

/** Road type of a piece (junction zones count as the street they open onto). */
export function roadTypeOf(pieceId: string): RoadType {
  const p = PIECES.find((q) => q.id === pieceId);
  return p ? p.roadType : "street";
}

// ---------------------------------------------------------------------------
// Delivery path.

export function pathWaypoints(): PathWaypoint[] {
  const ringHw = (RING_OUT - RING_IN) / 2;
  const wps: PathWaypoint[] = [
    { x: 0, z: 0, halfWidth: A1_HW, piece: "A1", name: "start" },
    { x: 0, z: S1_Z - S1_LANE, halfWidth: S1_HW, piece: "J1", name: "j1", fillet: 3.0 },
    // Roundabout approach: the deflection onto the ring starts here.
    { ...JOIN.westIn.pts[0], halfWidth: S1_HW, piece: "S1", name: "s1", fillet: 3.0 },
  ];
  // Circulate counter-clockwise from the west join to the north leave (3rd exit).
  const arc = ringArc(JOIN.westIn.psi, JOIN.northOut.psi - 360, RING_LANE, 8);
  arc.forEach((p, i) => {
    wps.push({
      ...p,
      halfWidth: ringHw,
      piece: "R",
      name: i === 0 ? "r_in" : i === arc.length - 1 ? "r_out" : undefined,
      fillet: i === arc.length - 1 ? 3.0 : 0,
    });
  });
  wps.push(
    { ...JOIN.northOut.pts[1], halfWidth: S2_HW, piece: "S2", name: "s2", fillet: 3.0 },
    { x: S2_X + S2_LANE, z: A2_Z, halfWidth: A2_HW, piece: "J2", name: "j2", fillet: 2.5 },
    { x: DEST_X, z: A2_Z, halfWidth: A2_HW, piece: "A2", name: "dest" },
  );
  return wps;
}

export const TURNS: Array<{ at: string; kind: TurnKind; exit?: number; label: string }> = [
  { at: "j1", kind: "right", label: "Turn right" },
  { at: "s1", kind: "roundabout", exit: 3, label: "Roundabout: 3rd exit" },
  { at: "j2", kind: "left", label: "Turn left" },
  { at: "dest", kind: "arrive", label: "Deliver" },
];

// ---------------------------------------------------------------------------
// Static props (collidable) and decorative modules (renderer only).

export interface PropDef extends Obstacle {
  kind: string;
  /** Deliberate rule-break, e.g. "blocking" (see PropDoc.role). */
  role?: string;
}

function car(x: number, z: number, alongX: boolean, kind = "parked_car"): PropDef {
  return alongX
    ? { kind, x, z, halfW: 2.1, halfD: 0.9, height: 1.4 }
    : { kind, x, z, halfW: 0.9, halfD: 2.1, height: 1.4 };
}

function barrier(x: number, z: number, alongX: boolean, half: number): PropDef {
  return alongX
    ? { kind: "barrier", x, z, halfW: half, halfD: 0.3, height: 1.0 }
    : { kind: "barrier", x, z, halfW: 0.3, halfD: half, height: 1.0 };
}

export const PROPS: PropDef[] = [
  // A1: parked motorbikes on the left edge (warm-up).
  { kind: "parked_motorbike", x: -1.5, z: 4, halfW: 0.3, halfD: 0.9, height: 0.9 },
  { kind: "parked_motorbike", x: -1.5, z: 6.4, halfW: 0.3, halfD: 0.9, height: 0.9 },
  // S1: parked cars along both curbs (street only — never in alleys).
  // No parking within ~15 m of the junction (where a tight right turn exits).
  car(18, S1_Z - 3.6, true),
  car(24, S1_Z - 3.6, true),
  car(30, S1_Z - 3.6, true),
  car(40, S1_Z + 3.6, true),
  car(46, S1_Z + 3.6, true),
  car(70, S1_Z + 3.6, true),
  // S2: parked motorbikes on the left edge (the walking group uses the right).
  { kind: "parked_motorbike", x: S2_X - 3.1, z: 118, halfW: 0.35, halfD: 0.9, height: 0.9 },
  { kind: "parked_motorbike", x: S2_X - 3.1, z: 120.4, halfW: 0.35, halfD: 0.9, height: 0.9 },
  // Construction barriers closing the wrong branches (visible, never traps).
  barrier(-40, S1_Z, false, S1_HW),
  barrier(RING.x, 43, true, 3.0),
  barrier(147, S1_Z, false, 3.0),
  barrier(S2_X, 167, true, S2_HW),
];

export interface DecorDef {
  kind: string;
  x: number;
  z: number;
  /** Facing (rad from +z), logical frame. */
  yaw: number;
}

export const DECOR: DecorDef[] = [
  { kind: "food_shop", x: -4.2, z: 1, yaw: Math.PI / 2 },
  { kind: "tree", x: 3.0, z: 20, yaw: 0 },
  { kind: "gate", x: -3.2, z: 30, yaw: Math.PI / 2 },
  { kind: "stall", x: 3.1, z: 44, yaw: -Math.PI / 2 },
  { kind: "tree", x: -3.0, z: 58, yaw: 0 },
  { kind: "planter", x: 3.0, z: 72, yaw: 0 },
  { kind: "sign", x: 2.6, z: 79, yaw: 0 },
  { kind: "stall", x: 30, z: S1_Z + 6.2, yaw: Math.PI },
  { kind: "tree", x: 55, z: S1_Z - 6.2, yaw: 0 },
  { kind: "apartment", x: 78, z: S1_Z + 7.5, yaw: Math.PI },
  { kind: "tree", x: RING.x, z: RING.z, yaw: 0 },
  { kind: "tree", x: S2_X - 5.5, z: 108, yaw: 0 },
  { kind: "stall", x: S2_X + 5.3, z: 126, yaw: -Math.PI / 2 },
  { kind: "gate", x: 90, z: A2_Z + 3.4, yaw: Math.PI },
  { kind: "tree", x: 70, z: A2_Z - 3.2, yaw: 0 },
  { kind: "planter", x: 52, z: A2_Z + 3.0, yaw: 0 },
  { kind: "customer", x: DEST_X, z: A2_Z + 2.4, yaw: Math.PI },
];

// ---------------------------------------------------------------------------
// Sections + encounters (zones expressed relative to path anchors).

export interface MissionSection {
  section: number;
  name: string;
  piece: string;
  sStart: number;
  sEnd: number;
}

interface Zone {
  from: [string, number];
  to: [string, number];
}

interface EncounterDef {
  id: string;
  type: string;
  section: number;
  zone: Zone;
  research: boolean;
  agentIds: string[];
}

export const SECTION_DEFS: Array<{ name: string; piece: string; from: [string, number]; to: [string, number] }> = [
  { name: "Alley warm-up", piece: "A1", from: ["start", 0], to: ["start", 14] },
  { name: "Research pedestrian", piece: "A1", from: ["start", 14], to: ["start", 50] },
  { name: "Alley traffic", piece: "A1", from: ["start", 50], to: ["j1", -2] },
  { name: "Turn right", piece: "J1", from: ["j1", -2], to: ["j1", 8] },
  { name: "Street", piece: "S1", from: ["j1", 8], to: ["s1", -8] },
  { name: "Roundabout", piece: "R", from: ["s1", -8], to: ["r_out", 6] },
  { name: "Street 2", piece: "S2", from: ["r_out", 6], to: ["j2", -2] },
  { name: "Turn left", piece: "J2", from: ["j2", -2], to: ["j2", 6] },
  { name: "Alley 2", piece: "A2", from: ["j2", 6], to: ["dest", -12] },
  { name: "Final approach", piece: "A2", from: ["dest", -12], to: ["dest", 0] },
];

export const ENCOUNTER_DEFS: EncounterDef[] = [
  { id: "e01_warmup", type: "warmup_props", section: 1, zone: { from: ["start", 0], to: ["start", 13] }, research: false, agentIds: ["pair_a", "pair_b"] },
  { id: "e02_research", type: "research_pedestrian", section: 2, zone: { from: ["start", 14], to: ["start", 50] }, research: true, agentIds: ["ped_research"] },
  { id: "e03_cat", type: "cat_crossing", section: 3, zone: { from: ["start", 51], to: ["start", 66] }, research: false, agentIds: ["cat"] },
  { id: "e04_alley_bike", type: "oncoming_motorbike", section: 3, zone: { from: ["start", 67], to: ["j1", -3] }, research: false, agentIds: ["bike_alley"] },
  { id: "e05_turn_right", type: "junction_turn", section: 4, zone: { from: ["j1", -2], to: ["j1", 7] }, research: false, agentIds: [] },
  { id: "e06_pullout", type: "car_pullout", section: 5, zone: { from: ["j1", 8], to: ["j1", 42] }, research: false, agentIds: ["car_pullout"] },
  { id: "e07_traffic", type: "street_traffic", section: 5, zone: { from: ["j1", 43], to: ["s1", -9] }, research: false, agentIds: ["bike_overtaker", "bike_oncoming_1", "moto_oncoming_1", "walker_street"] },
  { id: "e08_roundabout", type: "roundabout", section: 6, zone: { from: ["s1", -8], to: ["r_out", 5] }, research: false, agentIds: ["ring_bike_1", "ring_bike_2"] },
  { id: "e09_group", type: "walking_group", section: 7, zone: { from: ["r_out", 6], to: ["j2", -3] }, research: false, agentIds: ["group_1", "group_2", "group_3", "group_4", "moto_oncoming_2"] },
  { id: "e10_turn_left", type: "junction_turn", section: 8, zone: { from: ["j2", -2], to: ["j2", 6] }, research: false, agentIds: [] },
  { id: "e11_dog", type: "dog", section: 9, zone: { from: ["j2", 7], to: ["j2", 24] }, research: false, agentIds: ["dog"] },
  { id: "e12_cart", type: "vendor_cart", section: 9, zone: { from: ["j2", 25], to: ["dest", -13] }, research: false, agentIds: ["cart"] },
  { id: "e13_final", type: "combined_final", section: 10, zone: { from: ["dest", -12], to: ["dest", 0] }, research: false, agentIds: ["stander_final", "ped_final"] },
];

/** Research pedestrian: spawn / walk-trigger progress (relative to start). */
export const MISSION_PED_SPAWN_S = 48;
export const MISSION_RESEARCH_TRIGGER_S = 14;

// ---------------------------------------------------------------------------

export interface MissionBuild {
  mapId: string;
  network: RoadNetwork;
  path: DeliveryPath;
  obstacles: PropDef[];
  decor: DecorDef[];
  encounters: EncounterConfig[];
  sections: MissionSection[];
  destination: Vec2;
  mirror: 1 | -1;
  /** Ambience the map declares (ADR-054); absent means clear daylight. */
  conditions?: { timeOfDay?: "day" | "dusk" | "night"; weather?: string; intensity?: string };
  /** A photograph of the real ground, laid where it was taken (ADR-098). */
  imagery?: { url: string; originXZ: [number, number]; mpp: number; lift?: number; only?: boolean };
  makeAgents(): AgentModel[];
}

export function buildMission(cfg: ScenarioConfig): MissionBuild {
  const m: 1 | -1 = cfg.mirror ? -1 : 1;
  const M = (p: Vec2): Vec2 => ({ x: p.x * m, z: p.z });
  const MA = (pts: Vec2[]): Vec2[] => pts.map(M);
  const yawM = (yaw: number): number => (m > 0 ? yaw : Math.atan2(-Math.sin(yaw), Math.cos(yaw)));

  const network = new RoadNetwork(
    PIECES.map((p) =>
      p.kind === "band"
        ? { ...p, pts: MA(p.pts) }
        : { ...p, cx: p.cx * m, dir: (p.dir * m) as 1 | -1 },
    ),
  );
  // Kerb-side delivery (ADR-065 amendment K). The route's raw last waypoint
  // ("dest") is written on A2's centreline; the delivery is made at the kerb,
  // so it moves onto the painted lane before the path is built. The exported
  // district JSON keeps the same raw coordinate and gets the same snap in
  // compileMap, which is what holds the two descriptions in lockstep.
  const wps = pathWaypoints().map((w) => ({ ...w, x: w.x * m }));
  const kerbEnd = snapLastToKerb(wps, network);
  const path = new DeliveryPath(
    wps,
    TURNS.map((t) => {
      const kind = m > 0 ? t.kind : t.kind === "left" ? "right" : t.kind === "right" ? "left" : t.kind;
      const label = kind === "left" ? "Turn left" : kind === "right" ? "Turn right" : t.label;
      return { ...t, kind, label };
    }),
  );
  const S = ([anchor, off]: [string, number]): number => path.anchor(anchor) + off;

  const sections: MissionSection[] = SECTION_DEFS.map((d, i) => ({
    section: i + 1,
    name: d.name,
    piece: d.piece,
    sStart: S(d.from),
    sEnd: S(d.to),
  }));
  const encounters: EncounterConfig[] = ENCOUNTER_DEFS.map((e) => {
    const zoneStart = S(e.zone.from);
    const zoneEnd = S(e.zone.to);
    const piece = path.point((zoneStart + zoneEnd) / 2).piece;
    const roadType: RoadType = piece === "J1" ? "alley" : piece === "J2" ? "street" : roadTypeOf(piece);
    return { id: e.id, type: e.type, section: e.section, zoneStart, zoneEnd, research: e.research, agentIds: e.agentIds, roadType };
  });

  const obstacles: PropDef[] = PROPS.map((p) => ({ ...p, x: p.x * m }));
  const decor: DecorDef[] = DECOR.map((d) => ({ ...d, x: d.x * m, yaw: yawM(d.yaw) }));
  const destination: Vec2 = { x: kerbEnd.x, z: kerbEnd.z };

  const variant: Variant = cfg.variant;
  const seed = cfg.seed;
  const j1 = path.anchor("j1");
  const s1 = path.anchor("s1");
  const rOut = path.anchor("r_out");
  const j2 = path.anchor("j2");
  const dest = path.anchor("dest");

  const makeAgents = (): AgentModel[] => [
    // --- A1 -----------------------------------------------------------------
    new StandingPersonAgent("pair_a", "talking", ...xz(M({ x: 1.2, z: 8.6 })), yawM(0.3)),
    new StandingPersonAgent("pair_b", "talking", ...xz(M({ x: 1.3, z: 9.6 })), yawM(Math.PI - 0.3)),
    new ResearchPedAgent("ped_research", MISSION_PED_SPAWN_S, variant, m, seed, path, MISSION_RESEARCH_TRIGGER_S),
    // The oncoming alley bike ARRIVES: it waits kerbside on the cross-street
    // (occluded by the corner), turns into the alley on cue, and is already
    // rolling by the time the robot can see it. It used to stand dead in
    // the junction mouth and spring to life as the robot approached, which
    // play-testing rightly called staged. Cued at s = 21, which is 46 m
    // before e04's zone opens (start + 67), so it has turned in and is at
    // speed by the time the meeting happens — and the meeting is still
    // inside the zone.
    new VehicleAgent({
      id: "bike_alley",
      type: "motorbike",
      role: "oncoming",
      waypoints: MA([
        { x: -15, z: 87.6 },
        { x: -4, z: 84.5 },
        { x: -0.7, z: 80 },
        { x: -0.7, z: -6 },
      ]),
      cruise: 3.0,
      triggerS: 21,
    }),
    // Cued ~18 m out: at arcade pace a cat needs that to cross IN FRONT; any
    // closer and it (correctly) waits and dashes behind the robot instead.
    new CatAgent("cat", M({ x: A1_HW - 0.3, z: 62 }), M({ x: -A1_HW + 0.3, z: 62 }), 62, 42),
    // --- S1 -----------------------------------------------------------------
    new VehicleAgent({
      id: "car_pullout",
      type: "car",
      role: "pullout",
      waypoints: MA([
        { x: 38, z: S1_Z - 3.6 },
        { x: 45, z: S1_Z - 2.5 },
        { x: 53, z: S1_Z - S1_LANE },
        ...JOIN.westIn.pts,
        ...ringArc(JOIN.westIn.psi, JOIN.southOut.psi, RING_LANE, 8).slice(1),
        JOIN.southOut.pts[1],
        { x: RING.x - 1.2, z: 50 },
      ]),
      cruise: 6.0,
      // Cued while the robot is still in the alley so the pull-out happens
      // AHEAD of it (24+ m); a robot already close behind makes it hold.
      triggerS: j1 - 6,
      cueDelayS: 1.6,
      holdIfRobotBehindM: 18,
    }),
    new VehicleAgent({
      id: "bike_overtaker",
      type: "motorbike",
      role: "overtaker",
      waypoints: MA([
        { x: -22, z: S1_Z - S1_LANE },
        ...JOIN.westIn.pts,
        ...ringArc(JOIN.westIn.psi, JOIN.eastOut.psi - 360, RING_LANE, 8).slice(1),
        JOIN.eastOut.pts[1],
        { x: 140, z: S1_Z - 1.2 },
      ]),
      // Fast enough to be well clear before the roundabout (it takes the
      // 2nd exit; the robot the 3rd) - vehicles never cut across the robot.
      cruise: 11.0,
      triggerS: j1 - 4,
      canOvertake: true,
    }),
    new VehicleAgent({
      id: "bike_oncoming_1",
      type: "motorbike",
      role: "oncoming",
      waypoints: MA([{ x: 90, z: S1_Z + 2.0 }, { x: -32, z: S1_Z + 2.0 }]),
      cruise: 5.5,
      triggerS: j1 + 4,
    }),
    new VehicleAgent({
      id: "moto_oncoming_1",
      type: "motorcycle",
      role: "oncoming",
      waypoints: MA([{ x: 92, z: S1_Z + 2.0 }, { x: -32, z: S1_Z + 2.0 }]),
      cruise: 5.5, // same pace as the bike ahead of it in this lane: never drives through it
      triggerS: j1 + 34,
    }),
    new WalkerAgent("walker_street", "edge", MA([{ x: 52, z: S1_Z - 3.8 }, { x: 88, z: S1_Z - 3.8 }]), 1.3, j1 + 30),
    // --- Roundabout ---------------------------------------------------------
    new VehicleAgent({
      id: "ring_bike_1",
      type: "motorbike",
      role: "circulating",
      waypoints: MA([
        { x: RING.x + 1.2, z: 57 },
        ...JOIN.southIn.pts,
        ...ringArc(JOIN.southIn.psi, JOIN.northOut.psi, RING_LANE, 8).slice(1),
        { x: S2_X + 0.6, z: 100 },
        { x: S2_X + 0.6, z: 158 },
      ]),
      cruise: 5.5,
      triggerS: s1 - 30,
    }),
    new VehicleAgent({
      id: "ring_bike_2",
      type: "motorcycle",
      role: "circulating",
      waypoints: MA([
        { x: 130, z: S1_Z + 1.2 },
        ...JOIN.eastIn.pts,
        ...ringArc(JOIN.eastIn.psi, JOIN.southOut.psi, RING_LANE, 8).slice(1),
        JOIN.southOut.pts[1],
        { x: RING.x - 1.2, z: 50 },
      ]),
      cruise: 5.0,
      // Head start so it is circulating past the west entry when the robot
      // arrives at arcade pace: the robot must yield to ring traffic.
      triggerS: s1 - 58,
    }),
    // --- S2 -----------------------------------------------------------------
    ...[0, 1, 2, 3].map(
      (i) =>
        new WalkerAgent(
          `group_${i + 1}`,
          "group",
          MA([
            { x: S2_X + 2.7 - (i % 2) * 0.55, z: 100 + i * 0.9 },
            { x: S2_X + 2.7 - (i % 2) * 0.55, z: 133 + i * 0.9 },
          ]),
          1.2,
          rOut - 12,
        ),
    ),
    new VehicleAgent({
      id: "moto_oncoming_2",
      type: "motorcycle",
      role: "oncoming",
      waypoints: MA([
        { x: S2_X + S2_ONCOMING, z: 160 },
        ...JOIN.northIn.pts,
        ...ringArc(JOIN.northIn.psi, JOIN.westOut.psi, RING_LANE, 8).slice(1),
        JOIN.westOut.pts[1],
        { x: -32, z: S1_Z - S1_ONCOMING },
      ]),
      cruise: 7.0,
      triggerS: rOut + 2,
    }),
    // --- A2 -----------------------------------------------------------------
    new DogAgent("dog", M({ x: 88, z: A2_Z + A2_HW - 0.3 }), m, j2 + 7, path),
    new VehicleAgent({
      id: "cart",
      type: "vendor_cart",
      role: "pushing",
      // Parks short of the person waiting at x = 58 (no mutual avoidance).
      waypoints: MA([{ x: 78, z: A2_Z + 0.9 }, { x: 64, z: A2_Z + 0.9 }]),
      cruise: 0.8,
      triggerS: j2 - 20,
      cueDelayS: 1.0, // vendor grips the handle and leans in before pushing off
      parkAtEnd: true,
    }),
    new StandingPersonAgent("stander_final", "waiting", ...xz(M({ x: 58, z: A2_Z + 1.2 })), yawM(-Math.PI / 2)),
    new WalkerAgent("ped_final", "oncoming", MA([{ x: 42, z: A2_Z - 0.8 }, { x: 72, z: A2_Z - 0.8 }]), 1.2, dest - 26),
  ];

  return {
    mapId: MISSION_MAP_ID,
    network,
    path,
    obstacles,
    decor,
    encounters,
    sections,
    destination,
    mirror: m,
    makeAgents,
  };
}

function xz(p: Vec2): [number, number] {
  return [p.x, p.z];
}
